/**
 * Shared presentation for the affiliate portal — formatting, stat cells, form
 * fields, and the program's headline terms. Kept in one place so every section
 * of the portal reads as the same product.
 */
import { useOutletContext } from 'react-router-dom';
import { classNames } from '~/utils/classNames';
import type { AffiliateDashboard } from '~/lib/api/affiliates';

/** Canonical flat card — matches the rest of the app (Projects, Domains). */
export const CARD = 'rounded-xl border border-hairline bg-surface';

export function money(cents: number, currency = 'usd'): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

/** "42%" — a step-to-step conversion rate, or an em dash when there's no base. */
export function rate(part: number, whole: number): string {
  if (!whole) {
    return '—';
  }

  return `${Math.round((part / whole) * 100)}%`;
}

/** "Jul 15, 2026" — a short absolute date, or a fallback dash. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }

  const date = new Date(iso);

  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export const KIND_LABEL: Record<string, string> = {
  subscription: 'Subscription',
  credit_topup: 'Credit top-up',
  credit_pack: 'Credit pack',
};

export const COMMISSION_TONE: Record<string, string> = {
  earned: 'text-success border-success/30',
  void: 'text-muted border-subtle',
};

/** Display labels — the stored values are lowercase state names, not copy. */
export const COMMISSION_LABEL: Record<string, string> = {
  earned: 'Earned',
  void: 'Reversed',
};

export const PAYOUT_TONE: Record<string, string> = {
  requested: 'text-warning border-warning/30',
  approved: 'text-info border-info/30',
  paid: 'text-success border-success/30',
  rejected: 'text-danger border-danger/30',
};

/** The three headline terms of the program. */
export const TERMS = [
  { value: '20%', caption: 'of every payment' },
  { value: '6 months', caption: 'from their signup' },
  { value: 'Recurring', caption: 'renewals included' },
];

/** The portal's shared data, provided once by the layout. */
export interface PortalContext {
  data: AffiliateDashboard;
}

export function usePortal(): PortalContext {
  return useOutletContext<PortalContext>();
}

// ── Building blocks ──────────────────────────────────────────────────────────

/** A page heading inside the portal. */
export function PageHead({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <header className="mb-7">
      <h1 className="ui-serif text-[26px] leading-tight text-primary sm:text-[30px]">{title}</h1>
      {subtitle && <p className="mt-1.5 max-w-2xl text-[13px] leading-relaxed text-secondary">{subtitle}</p>}
    </header>
  );
}

/**
 * Newsprint-column strip: cells over a hairline background, the 1px grid gaps
 * drawing crisp dividers between them.
 */
export function CellGrid({ columns, children }: { columns: 2 | 3 | 4; children: React.ReactNode }) {
  return (
    <div
      className={classNames(
        'grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-hairline bg-hairline',
        // From md the portal sidebar takes 256px, so multi-column strips wait for lg — at 768–1023 the
        // money values overflowed 144px cells.
        columns === 2
          ? 'sm:grid-cols-2'
          : columns === 3
            ? 'sm:grid-cols-3 md:grid-cols-1 lg:grid-cols-3'
            : 'sm:grid-cols-2 lg:grid-cols-4',
      )}
    >
      {children}
    </div>
  );
}

export function Cell({
  label,
  value,
  note,
  accent,
}: {
  label: string;
  value: string | number;
  note?: string;
  accent?: boolean;
}) {
  return (
    <div className="bg-surface px-5 py-4">
      <div className="ui-caption text-muted">{label}</div>
      <div
        className={classNames(
          'mt-1.5 text-[26px] font-medium leading-none [overflow-wrap:anywhere]',
          accent ? 'text-accent' : 'text-primary',
        )}
      >
        {value}
      </div>
      {note && <div className="mt-2 text-[11.5px] text-faint">{note}</div>}
    </div>
  );
}

/** A titled panel with an optional right-hand slot in its header. */
export function Panel({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className={classNames(CARD, 'overflow-hidden')}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline px-4 py-3.5 sm:px-5">
        <h2 className="text-[13px] font-medium text-primary">{title}</h2>
        {aside}
      </div>
      {children}
    </div>
  );
}

export function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="px-5 py-12 text-center">
      <p className="text-[13px] text-secondary">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-[12px] leading-relaxed text-muted">{body}</p>
    </div>
  );
}

export function Field({
  label,
  children,
  required,
  optional,
  counter,
  error,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  optional?: boolean;
  counter?: string;
  error?: string;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-baseline justify-between gap-2">
        <span className="flex items-baseline gap-2">
          <span className="ui-caption text-secondary">{label}</span>
          {required && <span className="text-[10px] text-accent">Required</span>}
          {optional && <span className="text-[10px] text-faint">Optional</span>}
        </span>
        {counter && <span className="ui-mono text-[10px] text-faint">{counter}</span>}
      </span>
      {children}
      {error && <span className="text-[11px] text-danger">{error}</span>}
    </label>
  );
}

export function TextField({
  label,
  placeholder,
  value,
  onChange,
  error,
  optional,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  optional?: boolean;
}) {
  return (
    <Field label={label} optional={optional} error={error}>
      <input
        type="text"
        autoComplete="off"
        spellCheck={false}
        className={classNames('ui-input', error && 'border-danger/50')}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}
